import { useTypedQueryParams, UseTypedQueryParamsResult } from '../../hooks/useTypedQueryParams';
import { z } from 'zod';
import { OpportunityStatus } from '../../gql/graphql';
import { IfEquals } from '../../types/IfEquals';
import { useEffect, useState } from 'react';

export const statusOptions = ['new', 'open', 'postponed', 'resolved', 'archived'] as const;

true satisfies IfEquals<OpportunityStatus, (typeof statusOptions)[number], true, false>;

const filtersQueryParamsSchema = z.object({
	filters: z
		.object({
			journeyId: z.string().optional(),
			date: z
				.object({
					start: z.string().datetime({
						offset: true,
					}),
					end: z.string().datetime({
						offset: true,
					}),
				})
				.optional(),
			benefit: z.array(z.string()).optional(),
			tags: z.array(z.string()).optional(),
			owners: z.array(z.string()).optional(),
			status: z.array(z.enum(statusOptions)).optional(),
		})
		.optional(),
});

type OpportunityFilters = NonNullable<z.infer<typeof filtersQueryParamsSchema>['filters']>;

const useOpportunityFilters = (): UseTypedQueryParamsResult<OpportunityFilters> => {
	const [params, setParams] = useTypedQueryParams(filtersQueryParamsSchema);
	const [defaultFilters, setDefaultFilters] = useState<OpportunityFilters>(() => {
		const storedValue = sessionStorage.getItem('opportunitiesDefaultFilters');
		return storedValue
			? JSON.parse(storedValue)
			: {
					status: ['new', 'open', 'postponed'],
				};
	});

	const filters = { ...defaultFilters, ...params.filters };

	const setFilters = (filters: OpportunityFilters) => {
		setParams({ filters });
		setDefaultFilters({});
	};

	useEffect(() => {
		if (params.filters) {
			setDefaultFilters({});
		}
	}, [params.filters]);

	useEffect(() => {
		sessionStorage.setItem('opportunitiesDefaultFilters', JSON.stringify(defaultFilters));
	}, [defaultFilters]);

	return [filters, setFilters];
};

export { useOpportunityFilters, type OpportunityFilters };
